import { Button } from "PFComponents/button";
import { DataTableColumn } from "PFComponents/data_table/data_table";
import Toggle from "PFComponents/toggle/toggle";
import TooltipIcon from "PFComponents/tooltip_icon/tooltip_icon";
import InfoIcon from "PFIcons/info.svg";
import React from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BoolFieldName, Template } from "../types";
import css from "./styles.module.scss";
import { ToggleAction, ToggleIdentifier } from "./types";

const columnsDisabledByDefault: BoolFieldName[] = ["allowRmToEdit", "allowRmToEnable"];

const useColumns = (
  changedToggle: ToggleIdentifier | null,
  openConfirmModal: (rowId: number) => void,
  goToEditPage: (id: number) => void,
  onToggleClick: (action: ToggleAction) => void
): DataTableColumn[] => {
  const { t: defaultT } = useTranslation();
  const { t } = useTranslation("interestQuestions");

  const renderToggle = useCallback(
    (row: Template, columnName: BoolFieldName, warnWithModal?: boolean): JSX.Element => {
      const isChangedByUser = changedToggle?.rowId === row.id && changedToggle?.columnName === columnName;
      const isDisabled = !row.showQuestions && columnsDisabledByDefault.includes(columnName);

      return (
        <div className={css.cellCentered}>
          <Toggle
            onChange={(newValue) => {
              if (warnWithModal) {
                openConfirmModal(row.id);
              } else {
                onToggleClick({
                  rowId: row.id,
                  columnName,
                  newValue
                });
              }
            }}
            checked={isChangedByUser ? !row[columnName] : row[columnName]}
            disabled={isDisabled || !!changedToggle}
            inline
          />
        </div>
      );
    },
    [changedToggle, onToggleClick, openConfirmModal, goToEditPage]
  );

  const renderHeader = useCallback(
    (width: number, title: string, tooltip?: string): JSX.Element => (
      <div className={css.headerCentered} style={{ width }}>
        {title}
        {tooltip && <TooltipIcon IconComponent={InfoIcon} content={tooltip} height={14} width={14} />}
      </div>
    ),
    []
  );

  const columns: DataTableColumn[] = useMemo(
    () => [
      {
        headerName: t("templates.template"),
        renderCell: (row: Template) => t(`templates.${row.templateKey}` as unknown as TemplateStringsArray),
        tdStyle: { width: 160 }
      },
      {
        renderHeader: () =>
          renderHeader(130, t("templates.showQuestions.value"), t("templates.showQuestions.tooltip")),
        renderCell: (row: Template) => {
          const warnWithModal = !row.showQuestions && !row.question1 && !row.question2 && !row.question3;
          return renderToggle(row, "showQuestions", warnWithModal);
        }
      },
      {
        renderHeader: () =>
          renderHeader(
            135,
            t("templates.showStatementOfQualification.value"),
            t("templates.showStatementOfQualification.tooltip")
          ),
        renderCell: (row: Template) => renderToggle(row, "showStatement")
      },
      {
        renderHeader: () =>
          renderHeader(
            160,
            t("templates.allowRMToEnablePerRole.value"),
            t("templates.allowRMToEnablePerRole.tooltip")
          ),
        renderCell: (row: Template) => renderToggle(row, "allowRmToEnable")
      },
      {
        renderHeader: () =>
          renderHeader(120, t("templates.allowRMEdit.value"), t("templates.allowRMEdit.tooltip")),
        renderCell: (row: Template) => renderToggle(row, "allowRmToEdit")
      },
      {
        renderHeader: () =>
          renderHeader(
            190,
            t("templates.hideWhenRoleClosed.value"),
            t("templates.hideWhenRoleClosed.tooltip")
          ),
        renderCell: (row: Template) => renderToggle(row, "hideOnClosedRoles")
      },
      {
        renderHeader: () =>
          renderHeader(70, t("templates.questions.value"), t("templates.questions.tooltip")),
        renderCell: (row: Template) => (
          <div className={css.cellCentered}>
            <Button
              kind="secondary"
              onClick={() => {
                goToEditPage(row.id);
              }}
            >
              {defaultT("edit")}
            </Button>
          </div>
        )
      }
    ],
    [renderToggle, renderHeader, goToEditPage]
  );

  return columns;
};

export default useColumns;
